/**
 * Manager Dashboard Component
 * Team performance dashboard for managers, admins, and owners.
 * Shows summary stats, staff performance table, and non-active leads for reassignment.
 */

import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Checkbox,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
  Alert,
  Skeleton,
  Tooltip,
  SelectChangeEvent,
} from '@mui/material';
import {
  People,
  TrendingUp,
  AccountBalanceWallet,
  WarningAmber,
  DoNotDisturb,
  AssignmentInd,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/aiCrmApi';
import { teamApi, TeamMember } from '../api/teamApi';
import { showToast } from '../utils/toast';

// --- Types ---

interface TeamPerformanceSummary {
  totalTeamLeads: number;
  totalConversions: number;
  totalPipelineValue: number;
  overdueFollowUps: number;
  nonActiveLeadCount: number;
}

interface StaffMetric {
  userId: string;
  userName: string;
  userEmail: string;
  totalLeads: number;
  activeLeads: number;
  converted: number;
  needsFollowUp: number;
  completedRevenue: number;
  pipelineValue: number;
}

interface NonActiveLead {
  id: string;
  status: string;
  assignedToUserId: string;
  potentialValue: number;
  lastContactedAt: string;
}

interface TeamPerformanceData {
  summary: TeamPerformanceSummary;
  staffMetrics: StaffMetric[];
  nonActiveLeads: NonActiveLead[];
}

// --- Helpers ---

const formatCurrency = (value: number): string => {
  return `HK$${value.toLocaleString('en-HK', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return 'Never';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-HK', { year: 'numeric', month: 'short', day: 'numeric' });
};

// --- Stat Card Colors ---

const statCardStyles: Record<string, { bg: string; iconColor: string }> = {
  blue: { bg: 'rgba(33, 150, 243, 0.1)', iconColor: '#2196F3' },
  green: { bg: 'rgba(76, 175, 80, 0.1)', iconColor: '#4CAF50' },
  cyan: { bg: 'rgba(0, 188, 212, 0.1)', iconColor: '#00BCD4' },
  red: { bg: 'rgba(244, 67, 54, 0.1)', iconColor: '#F44336' },
  orange: { bg: 'rgba(255, 152, 0, 0.1)', iconColor: '#FF9800' },
};

// --- Sorting ---

type SortField = keyof StaffMetric;
type SortOrder = 'asc' | 'desc';

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  const valA = a[orderBy];
  const valB = b[orderBy];
  if (valB < valA) return -1;
  if (valB > valA) return 1;
  return 0;
}

function getComparator(order: SortOrder, orderBy: SortField) {
  return order === 'desc'
    ? (a: StaffMetric, b: StaffMetric) => descendingComparator(a, b, orderBy)
    : (a: StaffMetric, b: StaffMetric) => -descendingComparator(a, b, orderBy);
}

// --- Component ---

const ManagerDashboard: React.FC = () => {
  const queryClient = useQueryClient();

  // Sort state for staff table
  const [sortField, setSortField] = useState<SortField>('totalLeads');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  // Non-active leads selection
  const [selectedLeadIds, setSelectedLeadIds] = useState<Set<string>>(new Set());
  const [bulkAssigneeId, setBulkAssigneeId] = useState('');
  const [singleAssigneeMap, setSingleAssigneeMap] = useState<Record<string, string>>({});

  // Fetch team performance data
  const {
    data: perfData,
    isLoading: perfLoading,
    error: perfError,
  } = useQuery<TeamPerformanceData>({
    queryKey: ['ai-crm', 'dashboard', 'team-performance'],
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean; data: TeamPerformanceData }>(
        '/dashboard/team-performance'
      );
      return response.data.data;
    },
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Fetch team members for assignment dropdowns
  const { data: teamMembers } = useQuery<TeamMember[]>({
    queryKey: ['ai-crm', 'team', 'members'],
    queryFn: () => teamApi.getMembers(),
    staleTime: 5 * 60 * 1000,
  });

  // Mutation for reassigning a lead
  const reassignLead = useMutation({
    mutationFn: async ({ leadId, assignedToUserId }: { leadId: string; assignedToUserId: string }) => {
      const response = await apiClient.put(`/leads/${leadId}`, { assignedToUserId });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-crm', 'dashboard', 'team-performance'] });
      queryClient.invalidateQueries({ queryKey: ['ai-crm', 'leads'] });
    },
  });

  // Sorted staff metrics
  const sortedStaff = useMemo(() => {
    if (!perfData?.staffMetrics) return [];
    return [...perfData.staffMetrics].sort(getComparator(sortOrder, sortField));
  }, [perfData?.staffMetrics, sortField, sortOrder]);

  // Active team members for assignment
  const activeMembers = useMemo(() => {
    return (teamMembers || []).filter((m) => m.isActive);
  }, [teamMembers]);

  // --- Handlers ---

  const handleSortRequest = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const handleToggleSelect = (leadId: string) => {
    setSelectedLeadIds((prev) => {
      const next = new Set(prev);
      if (next.has(leadId)) {
        next.delete(leadId);
      } else {
        next.add(leadId);
      }
      return next;
    });
  };

  const handleToggleSelectAll = () => {
    if (!perfData?.nonActiveLeads) return;
    if (selectedLeadIds.size === perfData.nonActiveLeads.length) {
      setSelectedLeadIds(new Set());
    } else {
      setSelectedLeadIds(new Set(perfData.nonActiveLeads.map((l) => l.id)));
    }
  };

  const handleBulkAssign = async () => {
    if (!bulkAssigneeId || selectedLeadIds.size === 0) return;
    try {
      const promises = Array.from(selectedLeadIds).map((leadId) =>
        reassignLead.mutateAsync({ leadId, assignedToUserId: bulkAssigneeId })
      );
      await Promise.all(promises);
      showToast.statusUpdated();
      setSelectedLeadIds(new Set());
      setBulkAssigneeId('');
    } catch {
      showToast.operationFailed('Failed to reassign leads');
    }
  };

  const handleSingleAssign = async (leadId: string) => {
    const assigneeId = singleAssigneeMap[leadId];
    if (!assigneeId) return;
    try {
      await reassignLead.mutateAsync({ leadId, assignedToUserId: assigneeId });
      showToast.statusUpdated();
      setSingleAssigneeMap((prev) => {
        const next = { ...prev };
        delete next[leadId];
        return next;
      });
    } catch {
      showToast.operationFailed('Failed to reassign lead');
    }
  };

  // --- Render ---

  if (perfError) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        Failed to load team performance data. Please try again later.
      </Alert>
    );
  }

  const summary = perfData?.summary;

  return (
    <Box>
      {/* Section Title */}
      <Box mb={3} display="flex" alignItems="center" gap={1}>
        <People sx={{ color: '#2196F3', fontSize: 28 }} />
        <Typography variant="h5" fontWeight="bold">
          Team Performance Dashboard
        </Typography>
      </Box>

      {/* Summary Stat Cards */}
      <Grid container spacing={3} mb={4}>
        {/* Total Team Leads */}
        <Grid item xs={12} sm={6} md={2.4}>
          {perfLoading ? (
            <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 3 }} />
          ) : (
            <StatCard
              title="Total Team Leads"
              value={summary?.totalTeamLeads ?? 0}
              icon={<People />}
              colorKey="blue"
            />
          )}
        </Grid>

        {/* Total Conversions */}
        <Grid item xs={12} sm={6} md={2.4}>
          {perfLoading ? (
            <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 3 }} />
          ) : (
            <StatCard
              title="Total Conversions"
              value={summary?.totalConversions ?? 0}
              icon={<TrendingUp />}
              colorKey="green"
            />
          )}
        </Grid>

        {/* Total Pipeline Value */}
        <Grid item xs={12} sm={6} md={2.4}>
          {perfLoading ? (
            <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 3 }} />
          ) : (
            <StatCard
              title="Total Pipeline Value"
              value={formatCurrency(summary?.totalPipelineValue ?? 0)}
              icon={<AccountBalanceWallet />}
              colorKey="cyan"
            />
          )}
        </Grid>

        {/* Overdue Follow-ups */}
        <Grid item xs={12} sm={6} md={2.4}>
          {perfLoading ? (
            <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 3 }} />
          ) : (
            <StatCard
              title="Overdue Follow-ups"
              value={summary?.overdueFollowUps ?? 0}
              icon={<WarningAmber />}
              colorKey="red"
            />
          )}
        </Grid>

        {/* Non-Active Leads */}
        <Grid item xs={12} sm={6} md={2.4}>
          {perfLoading ? (
            <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 3 }} />
          ) : (
            <StatCard
              title="Non-Active Leads"
              value={summary?.nonActiveLeadCount ?? 0}
              icon={<DoNotDisturb />}
              colorKey="orange"
            />
          )}
        </Grid>
      </Grid>

      {/* Staff Performance Table */}
      <Typography variant="h6" fontWeight="bold" mb={2}>
        Staff Performance
      </Typography>
      {perfLoading ? (
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2, mb: 4 }} />
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            mb: 4,
            borderRadius: 2,
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 600, bgcolor: 'grey.50' } }}>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'userName'}
                    direction={sortField === 'userName' ? sortOrder : 'asc'}
                    onClick={() => handleSortRequest('userName')}
                  >
                    Staff Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'userEmail'}
                    direction={sortField === 'userEmail' ? sortOrder : 'asc'}
                    onClick={() => handleSortRequest('userEmail')}
                  >
                    Email
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'totalLeads'}
                    direction={sortField === 'totalLeads' ? sortOrder : 'asc'}
                    onClick={() => handleSortRequest('totalLeads')}
                  >
                    Assigned Leads
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'activeLeads'}
                    direction={sortField === 'activeLeads' ? sortOrder : 'asc'}
                    onClick={() => handleSortRequest('activeLeads')}
                  >
                    Active
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'converted'}
                    direction={sortField === 'converted' ? sortOrder : 'asc'}
                    onClick={() => handleSortRequest('converted')}
                  >
                    Converted
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'needsFollowUp'}
                    direction={sortField === 'needsFollowUp' ? sortOrder : 'asc'}
                    onClick={() => handleSortRequest('needsFollowUp')}
                  >
                    Follow-up Needed
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'pipelineValue'}
                    direction={sortField === 'pipelineValue' ? sortOrder : 'asc'}
                    onClick={() => handleSortRequest('pipelineValue')}
                  >
                    Pipeline Value (HK$)
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'completedRevenue'}
                    direction={sortField === 'completedRevenue' ? sortOrder : 'asc'}
                    onClick={() => handleSortRequest('completedRevenue')}
                  >
                    Completed Revenue (HK$)
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedStaff.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No staff data available</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedStaff.map((staff) => (
                  <TableRow
                    key={staff.userId}
                    hover
                    sx={{ '&:last-child td': { borderBottom: 0 } }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {staff.userName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {staff.userEmail}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{staff.totalLeads}</TableCell>
                    <TableCell align="right">{staff.activeLeads}</TableCell>
                    <TableCell align="right">
                      <Chip
                        label={staff.converted}
                        size="small"
                        color={staff.converted > 0 ? 'success' : 'default'}
                        variant={staff.converted > 0 ? 'filled' : 'outlined'}
                        sx={{ minWidth: 36 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={staff.needsFollowUp}
                        size="small"
                        sx={{
                          minWidth: 36,
                          bgcolor: staff.needsFollowUp > 0 ? 'rgba(244, 67, 54, 0.1)' : undefined,
                          color: staff.needsFollowUp > 0 ? '#D32F2F' : undefined,
                          fontWeight: staff.needsFollowUp > 0 ? 600 : 400,
                        }}
                        variant={staff.needsFollowUp > 0 ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {formatCurrency(staff.pipelineValue)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={500} color="success.main">
                        {formatCurrency(staff.completedRevenue)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Non-Active Leads Section */}
      <Box mb={2} display="flex" alignItems="center" gap={1}>
        <AssignmentInd sx={{ color: '#FF9800', fontSize: 24 }} />
        <Typography variant="h6" fontWeight="bold">
          Non-Active Leads (Available for Reassignment)
        </Typography>
        {perfData?.nonActiveLeads && (
          <Chip
            label={perfData.nonActiveLeads.length}
            size="small"
            color="warning"
            sx={{ ml: 1 }}
          />
        )}
      </Box>

      {perfLoading ? (
        <Skeleton variant="rectangular" height={250} sx={{ borderRadius: 2 }} />
      ) : perfData?.nonActiveLeads && perfData.nonActiveLeads.length > 0 ? (
        <>
          {/* Bulk Assignment Controls */}
          <Box
            display="flex"
            alignItems="center"
            gap={2}
            mb={2}
            p={2}
            sx={{
              bgcolor: 'grey.50',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 'fit-content' }}>
              {selectedLeadIds.size} selected
            </Typography>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Assign to...</InputLabel>
              <Select
                value={bulkAssigneeId}
                label="Assign to..."
                onChange={(e: SelectChangeEvent) => setBulkAssigneeId(e.target.value)}
              >
                {activeMembers.map((member) => (
                  <MenuItem key={member.id} value={member.id}>
                    {member.name} ({member.email})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              size="small"
              disabled={selectedLeadIds.size === 0 || !bulkAssigneeId || reassignLead.isPending}
              onClick={handleBulkAssign}
              startIcon={reassignLead.isPending ? <CircularProgress size={16} /> : <AssignmentInd />}
              sx={{ textTransform: 'none' }}
            >
              Assign ({selectedLeadIds.size})
            </Button>
          </Box>

          {/* Non-Active Leads Table */}
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: 2,
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 600, bgcolor: 'grey.50' } }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={
                        selectedLeadIds.size > 0 &&
                        selectedLeadIds.size < (perfData.nonActiveLeads?.length ?? 0)
                      }
                      checked={
                        (perfData.nonActiveLeads?.length ?? 0) > 0 &&
                        selectedLeadIds.size === (perfData.nonActiveLeads?.length ?? 0)
                      }
                      onChange={handleToggleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Lead ID</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Currently Assigned To</TableCell>
                  <TableCell align="right">Potential Value</TableCell>
                  <TableCell>Last Contacted</TableCell>
                  <TableCell sx={{ minWidth: 280 }}>Reassign To</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {perfData.nonActiveLeads.map((lead) => {
                  const assignedMember = activeMembers.find(
                    (m) => m.id === lead.assignedToUserId
                  );
                  return (
                    <TableRow
                      key={lead.id}
                      hover
                      selected={selectedLeadIds.has(lead.id)}
                      sx={{ '&:last-child td': { borderBottom: 0 } }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedLeadIds.has(lead.id)}
                          onChange={() => handleToggleSelect(lead.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title={lead.id}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {lead.id.slice(0, 8)}...
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={lead.status}
                          size="small"
                          variant="outlined"
                          color={
                            lead.status === 'LOST'
                              ? 'error'
                              : lead.status === 'UNQUALIFIED'
                              ? 'warning'
                              : 'default'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {assignedMember ? assignedMember.name : lead.assignedToUserId ? 'Unknown' : 'Unassigned'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {lead.potentialValue ? formatCurrency(lead.potentialValue) : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(lead.lastContactedAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <FormControl size="small" sx={{ minWidth: 160 }}>
                            <Select
                              value={singleAssigneeMap[lead.id] || ''}
                              displayEmpty
                              onChange={(e: SelectChangeEvent) =>
                                setSingleAssigneeMap((prev) => ({
                                  ...prev,
                                  [lead.id]: e.target.value,
                                }))
                              }
                              renderValue={(selected) => {
                                if (!selected) return <em style={{ color: '#999' }}>Select staff</em>;
                                const m = activeMembers.find((m) => m.id === selected);
                                return m ? m.name : selected;
                              }}
                            >
                              {activeMembers.map((member) => (
                                <MenuItem key={member.id} value={member.id}>
                                  {member.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <Button
                            variant="outlined"
                            size="small"
                            disabled={
                              !singleAssigneeMap[lead.id] || reassignLead.isPending
                            }
                            onClick={() => handleSingleAssign(lead.id)}
                            sx={{ textTransform: 'none', minWidth: 'auto' }}
                          >
                            Assign
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : (
        <Box
          sx={{
            p: 4,
            textAlign: 'center',
            bgcolor: 'rgba(0,0,0,0.02)',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <DoNotDisturb sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">
            No non-active leads available for reassignment.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// --- Stat Card sub-component ---

interface StatCardProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  colorKey: 'blue' | 'green' | 'cyan' | 'red' | 'orange';
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, colorKey }) => {
  const style = statCardStyles[colorKey];
  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: 3,
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box flex={1}>
            <Typography
              color="text.secondary"
              gutterBottom
              variant="body2"
              sx={{ fontWeight: 500, mb: 1 }}
            >
              {title}
            </Typography>
            <Typography
              variant="h4"
              component="div"
              fontWeight="bold"
              sx={{
                fontSize: typeof value === 'string' && value.length > 10 ? '1.25rem' : '1.75rem',
                lineHeight: 1.2,
              }}
            >
              {value}
            </Typography>
          </Box>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: style.bg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '& svg': {
                color: style.iconColor,
                fontSize: 24,
              },
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ManagerDashboard;
